<!-- Toolbar trigger button -->
@if (currentWorkspaceId()) {
  <button
    (click)="togglePalette()"
    [matTooltip]="getCurrentWorkspaceName() + ' (Ctrl+K)'"
    matTooltipPosition="below"
    class="toolbar-workspace-button"
    mat-button
    type="button">
    <div class="toolbar-workspace-button__indicator"
      [style.background-color]="getWorkspaceColor(getCurrentWorkspace())">
      <span class="toolbar-workspace-button__initials">{{ getWorkspaceInitials(getCurrentWorkspace()) }}</span>
    </div>
    <span class="toolbar-workspace-button__name hide-on-mobile">{{ getCurrentWorkspaceName() }}</span>
    <mat-icon class="toolbar-workspace-button__icon">expand_more</mat-icon>
  </button>
} @else {
  <button
    (click)="togglePalette()"
    [matTooltip]="'shared.workspace.select' | translate"
    matTooltipPosition="below"
    class="toolbar-workspace-button toolbar-workspace-button--empty"
    mat-button
    type="button">
    <div class="toolbar-workspace-button__indicator" style="background-color: rgb(var(--ds-danger));">
      <mat-icon class="text-white" style="font-size: 18px;">workspaces</mat-icon>
    </div>
    <span class="toolbar-workspace-button__name hide-on-mobile text-danger">{{ 'shared.workspace.select' | translate }}</span>
    <mat-icon class="toolbar-workspace-button__icon text-danger">expand_more</mat-icon>
  </button>
}

<!-- Power-user dense workspace selector overlay -->
<ng-template #paletteTpl>
  <div class="power" role="dialog" aria-modal="true">
    <header class="power__header">
      <span class="power__eyebrow">{{ 'shared.workspace.eyebrow' | translate }}</span>
      <h2 class="power__title">{{ 'shared.workspace.title2' | translate }}</h2>
      <p class="power__subtitle">{{ 'shared.workspace.subtitle' | translate }}</p>
    </header>

    <div class="power__search">
      <mat-icon class="power__search-icon">search</mat-icon>
      <input
        #searchInput
        type="text"
        class="power__search-input"
        autocomplete="off"
        spellcheck="false"
        [placeholder]="'shared.workspace.filterPlaceholder' | translate"
        [ngModel]="searchTerm()"
        (ngModelChange)="searchTerm.set($event); highlightedIndex.set(0)" />
      <span class="power__esc">esc</span>
    </div>

    <div class="power__table">
      <div class="power__thead">
        <button type="button" class="power__th power__th--ws" (click)="toggleSort('name')">
          <span>{{ 'shared.workspace.col.workspace' | translate }}</span>
          @if (sortKey() === 'name') {
            <mat-icon class="power__sort-icon">{{ sortDir() === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</mat-icon>
          }
        </button>
        <button type="button" class="power__th power__th--num" (click)="toggleSort('properties')">
          <span>{{ 'shared.workspace.col.properties' | translate }}</span>
          @if (sortKey() === 'properties') {
            <mat-icon class="power__sort-icon">{{ sortDir() === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</mat-icon>
          }
        </button>
        <span class="power__th power__th--num">{{ 'shared.workspace.col.rent' | translate }}</span>
        <span class="power__th power__th--num">{{ 'shared.workspace.col.cash' | translate }}</span>
        <span class="power__th power__th--num">{{ 'shared.workspace.col.inbox' | translate }}</span>
        <button type="button" class="power__th power__th--last" (click)="toggleSort('lastVisit')">
          <span>{{ 'shared.workspace.col.lastVisit' | translate }}</span>
          @if (sortKey() === 'lastVisit') {
            <mat-icon class="power__sort-icon">{{ sortDir() === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</mat-icon>
          }
        </button>
        <span class="power__th power__th--chev"></span>
      </div>

      <div class="power__tbody">
        @if (sortedRows().length === 0) {
          <div class="power__empty">
            <mat-icon>search_off</mat-icon>
            <span>{{ 'shared.workspace.noResults' | translate }}</span>
          </div>
        }

        @for (item of sortedRows(); track trackById($index, item)) {
          <button
            type="button"
            class="power-row"
            [class.power-row--highlighted]="isHighlighted(item)"
            [class.power-row--active]="isActive(item)"
            [disabled]="isSwitching()"
            (mouseenter)="onItemHover(sortedRows().indexOf(item))"
            (click)="activate(item)">

            <div class="power-row__ws">
              @if (switchingId() === item.id) {
                <div class="power-row__avatar power-row__avatar--loading">
                  <mat-spinner diameter="18"></mat-spinner>
                </div>
              } @else {
                <div class="power-row__avatar" [style.background-color]="item.color">
                  <span>{{ item.initials }}</span>
                </div>
              }
              <div class="power-row__ws-text">
                <div class="power-row__name-line">
                  <span class="power-row__name">{{ item.name }}</span>
                  @if (switchingId() === item.id) {
                    <span class="power-row__pill power-row__pill--active">
                      {{ 'shared.workspace.switching' | translate }}
                    </span>
                  } @else if (isActive(item)) {
                    <span class="power-row__pill power-row__pill--active">
                      <span class="power-row__pill-dot"></span>
                      {{ 'shared.workspace.active' | translate }}
                    </span>
                  } @else if (item.isShared) {
                    <span class="power-row__pill power-row__pill--shared">
                      <span class="power-row__pill-dot power-row__pill-dot--shared"></span>
                      {{ 'shared.workspace.shared' | translate }}
                    </span>
                  }
                </div>
                <div class="power-row__meta">
                  <span>{{ item.typeLabel }}</span>
                  <span class="power-row__sep">·</span>
                  <span>{{ item.role }}</span>
                </div>
              </div>
            </div>

            <div class="power-row__cell power-row__cell--num">
              @if (item.propertyCount > 0) {
                <span class="power-row__primary">{{ 'shared.workspace.propertiesCount' | translate:{ count: item.propertyCount } }}</span>
                @if (item.unitsCount !== null && item.unitsCount > 0) {
                  <span class="power-row__secondary">{{ 'shared.workspace.unitsShort' | translate:{ count: item.unitsCount } }}</span>
                }
              } @else {
                <span class="power-row__muted">—</span>
              }
            </div>

            <div class="power-row__cell power-row__cell--num">
              @if (item.monthlyRentEUR !== null) {
                <span class="power-row__primary">{{ formatMoneyShort(item.monthlyRentEUR) }}</span>
                <span class="power-row__secondary">{{ 'shared.workspace.perMonth' | translate }}</span>
              } @else {
                <span class="power-row__muted">—</span>
              }
            </div>

            <div class="power-row__cell power-row__cell--num">
              @if (item.cashOverdueEUR !== null && item.cashOverdueEUR > 0) {
                <span class="power-row__primary power-row__primary--danger">{{ formatMoneyShort(item.cashOverdueEUR) }}</span>
                <span class="power-row__secondary power-row__secondary--danger">{{ 'shared.workspace.overdue' | translate }}</span>
              } @else if (item.cashIsOnTrack === true) {
                <span class="power-row__primary power-row__primary--ok">
                  <mat-icon class="power-row__check">check</mat-icon>
                  {{ 'shared.workspace.onTrack' | translate }}
                </span>
              } @else {
                <span class="power-row__muted">—</span>
              }
            </div>

            <div class="power-row__cell power-row__cell--num">
              @if (item.inboxUrgentCount > 0) {
                <span class="power-row__urgent">
                  <mat-icon>warning</mat-icon>
                  {{ 'shared.workspace.urgentCount' | translate:{ count: item.inboxUrgentCount } }}
                </span>
              }
              @if (item.inboxTotalCount > 0) {
                <span class="power-row__secondary">{{ 'shared.workspace.mailsCount' | translate:{ count: item.inboxTotalCount } }}</span>
              } @else if (item.inboxUrgentCount === 0) {
                <span class="power-row__muted">—</span>
              }
            </div>

            <div class="power-row__cell power-row__cell--last">
              <span class="power-row__time">{{ formatRelativeTime(item.lastActivity) }}</span>
            </div>

            <div class="power-row__cell power-row__cell--chev">
              <mat-icon>chevron_right</mat-icon>
            </div>
          </button>
        }
      </div>
    </div>

    <footer class="power__footer">
      <button type="button" class="power__create" (click)="openCreateWorkspaceDialog()">
        <mat-icon>add</mat-icon>
        <span>{{ 'shared.workspace.createNew' | translate }}</span>
      </button>
      <span class="power__footer-meta">{{ 'shared.workspace.footerHint' | translate }}</span>
    </footer>
  </div>
</ng-template>
